import React, {useCallback, useEffect, useState} from 'react';
import landingUrl from 'constant/landingUrl';
import {useRouter} from 'next/router';
import TextField from '@mui/material/TextField';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import Head from 'next/head';
import parse from 'html-react-parser';
import ValidationEmailDialog from 'components/ValidationEmailDialog/ValidationEmailDialog';
import {useAuth} from 'lib/useAuth';
import {openCookiePreferences, TERMLY_PREFERENCES_CLASS} from 'utils/consent';
import {useAnalytics} from 'lib/use-analytics';
import {useEffectOnce} from 'lib/useEffectOnce';
import {getUnauthedDistinctId} from 'utils/mixpanelUtil';
import {transformEmail} from 'utils/emailUtil';

export default function Index() {
    const router = useRouter();
    const {loginRequestFeedback, isLoggingIn, signIn} = useAuth();
    const {track, AnalyticsEvent} = useAnalytics();
    const [email, setEmail] = useState(transformEmail((router.query.email as string) || ''));
    const [isEmailValid, setIsEmailValid] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);

    useEffectOnce(() => {
        const distinct_id_payload = {distinct_id: getUnauthedDistinctId(router)};
        const email = transformEmail(transformEmail((router.query.email as string) || ''));
        if (email) {
            validateEmail(email);
            track(AnalyticsEvent.PREAPPROVAL_RESULTS_VIEW, distinct_id_payload);
        }
        track(AnalyticsEvent.LOGIN_FORM, distinct_id_payload);
    });

    useEffect(() => {
        setModalOpen(!!loginRequestFeedback?.success);
    }, [loginRequestFeedback]);

    const onSubmit = useCallback(
        (event: any) => {
            event.preventDefault();
            if (router.query.email) {
                track(AnalyticsEvent.PREAPPROVAL_RESULTS_COMPLETE);
            }
            signIn(email);
            track(AnalyticsEvent.LOGIN_FORM_SUBMITTED);
        },
        [signIn, email, router.query.email],
    );

    const handleKeypress = useCallback(
        (event: React.KeyboardEvent<HTMLDivElement>) => {
            if (event.key === 'Enter' && isEmailValid) {
                onSubmit(event);
            }
        },
        [onSubmit],
    );

    const handleModalClose = useCallback(() => {
        setModalOpen(false);
    }, [setModalOpen]);

    const validateEmail = (value: string) => {
        const EMAIL_REGEX =
            /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        setIsEmailValid(EMAIL_REGEX.test(value.trim()));
    };
    const onEmailChange = (value: string) => {
        validateEmail(value);
        setEmail(value);
    };

    return (
        <>
            <Head>
                <title>Amplify - Login</title>
            </Head>
            <div className="container-fluid">
                <div className="row min-vh-100 wrapperLogin">
                    <div className="col login-sec">
                        <div className="col-3 mx-auto h-100 d-table top-section">
                            <div className="d-table-cell align-middle">
                                <div className="logo loginLogo">
                                    <a aria-label="Amplify Home Page" href={landingUrl} className="d-inline-block">
                                        <img src="/images/logo.svg" width="156px" height="37px" alt="" />
                                    </a>
                                </div>
                                <h2 className="mt-4 pt-4">
                                    {router.query.email ? (
                                        <>Your preapproval results are in!</>
                                    ) : (
                                        <>
                                            Welcome back to <span className="blue">Amplify!</span>
                                        </>
                                    )}
                                </h2>
                                <h5 className="mt-4 mb-4 pb-4">
                                    {router.query.email ? (
                                        <>Coming right up! For security, please click the link that we emailed you.</>
                                    ) : (
                                        <>Log in to continue exploring life insurance options.</>
                                    )}
                                </h5>
                                {loginRequestFeedback && loginRequestFeedback.success && (
                                    <ValidationEmailDialog email={email} open={modalOpen} onClose={handleModalClose} />
                                )}
                                {loginRequestFeedback && !loginRequestFeedback.success && (
                                    <div className="login-req-error-msg">
                                        <p>{parse(loginRequestFeedback.message)}</p>
                                    </div>
                                )}
                                <TextField
                                    label="Email Address"
                                    placeholder="looking4insurance@gmail.com"
                                    focused
                                    fullWidth
                                    value={email}
                                    variant="outlined"
                                    onChange={(e) => onEmailChange(e.target.value)}
                                    onKeyPress={handleKeypress}
                                    InputLabelProps={{
                                        style: {
                                            color: '#5AE288',
                                        },
                                    }}
                                    InputProps={{
                                        sx: {
                                            '& fieldset.MuiOutlinedInput-notchedOutline': {
                                                borderWidth: '1px',
                                                borderColor: '#5AE288 !important',
                                                borderRadius: '8px !important',
                                            },
                                        },
                                    }}
                                />
                                <button
                                    type="submit"
                                    className="btn btn-login w-100 mt-3"
                                    onClick={onSubmit}
                                    disabled={isLoggingIn || !isEmailValid}
                                >
                                    {isLoggingIn ? <>...Sending Login Link</> : <>Send Login Link</>}
                                </button>
                                <Typography variant="subtitle2" mt={3.5} color="#B1B1B1">
                                    No account yet?{' '}
                                    <Link
                                        href="https://quotes.getamplifylife.com/quotes"
                                        sx={{color: '#174943', textDecorationColor: '#174943'}}
                                    >
                                        Start an application
                                    </Link>{' '}
                                    or send us a message for support{' '}
                                    <Link
                                        href="mailto:team@getamplifylife.com"
                                        sx={{color: 'inherit', textDecorationColor: '#B1B1B1'}}
                                    >
                                        team@getamplifylife.com
                                    </Link>
                                </Typography>
                                {/* Reachable before logging in, where the drawer does not exist. */}
                                <Typography
                                    component="button"
                                    type="button"
                                    className={TERMLY_PREFERENCES_CLASS}
                                    onClick={openCookiePreferences}
                                    aria-label="Cookie preferences"
                                    sx={{
                                        background: 'none',
                                        border: 0,
                                        padding: 0,
                                        marginTop: 2,
                                        font: 'inherit',
                                        color: 'inherit',
                                        cursor: 'pointer',
                                        textDecoration: 'underline',
                                    }}
                                >
                                    Cookie preferences
                                </Typography>
                            </div>
                        </div>
                    </div>
                    <div className="col banner-sec">
                        <div className="col-3 mx-auto h-100 d-table">
                            <div className="d-table-cell align-middle">
                                <img className="d-block img-fluid" src="/images/05_welcome.png" alt="First slide" />
                                <h5 className="text-center">Rethink life insurance</h5>
                                <p className="text-center">Build wealth while protecting your family.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

Index.redirectOnAuthenticated = true;
